import type { HTMLAttributes, ReactNode } from "react";
import { Badge as CossBadge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export type BadgeVariant =
  | "status"
  | "classification"
  | "severity"
  | "alignment"
  | "source";

// Domain badge *shapes* (pill geometry + weight). Kept stable so the migration
// onto the Coss Badge primitive is a wrap, not a visual change: the Coss base
// supplies structure/focus/a11y, the domain tone map (toneClassName) supplies
// color, and these classes pin the pre-existing rounded-full pill look. `h-auto`
// neutralises the Coss default fixed height so sizing stays content-driven.
const badgeVariantClasses: Record<BadgeVariant, string> = {
  status:
    "h-auto rounded-full border px-3 py-1 text-xs font-semibold",
  classification:
    "h-auto rounded-full border px-2.5 py-1 text-xs font-bold",
  severity:
    "h-auto rounded-full border px-2.5 py-1 text-xs font-medium",
  alignment:
    "h-auto gap-1 rounded-full border px-2.5 py-1 text-xs font-medium",
  source:
    "h-auto rounded-full border px-2.5 py-1 text-xs font-medium",
};

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  children: ReactNode;
  toneClassName: string;
  variant: BadgeVariant;
}

export function Badge(
  { children, toneClassName, variant, className = "", ...props }: BadgeProps,
) {
  // Coss `outline` gives a neutral bordered base; the domain tone map overrides
  // its border/bg/text colors (tailwind-merge: later className wins).
  return (
    <CossBadge
      variant="outline"
      className={cn(badgeVariantClasses[variant], toneClassName, className)}
      {...props}
    >
      {children}
    </CossBadge>
  );
}
